// Traduccion entre la forma del formulario y la del API.
//
// Son dos formas distintas a proposito, y la traduccion vive AQUI, en un solo sitio:
//
//   · El formulario usa la forma del prototipo validado con el cliente —camelCase,
//     objetos anidados, cadenas vacias para "sin llenar"— porque esa pantalla es la
//     que se aprobo y ADR-011 manda reutilizarla, no rehacerla.
//   · El API usa snake_case, `null`/ausencia para "sin dato" y dominios cerrados.
//
// Repartir la traduccion por los componentes es como se pierden campos en silencio:
// basta que una seccion olvide mapear uno para que el `PUT` lo omita, y omitir
// significa "no lo toques", asi que el dato nunca llega y nadie ve un error.

/** Cadena vacia -> ausencia. El API distingue "no lo toques" de "borralo". */
import type { AnimalDelFormulario, CasoDelFormulario, EventoDelFormulario, PersonaDelFormulario, RespuestaDelApi } from '../tipos';
import { fechaIsoDeInstante, instanteDeMedianoche } from './fechas';

const texto = (v: unknown) => {
  const limpio = typeof v === 'string' ? v.trim() : v;
  return limpio || undefined;
};

const numero = (v: unknown) => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const entero = (v: unknown) => {
  const n = numero(v);
  return n === undefined ? undefined : Math.trunc(n);
};

/** 'Sí' / 'No' del formulario -> booleano del API. Sin respuesta -> ausencia. */
const siNo = (v: unknown) => {
  if (v === 'Sí' || v === 'Si' || v === 'si' || v === true) return true;
  if (v === 'No' || v === 'no' || v === false) return false;
  return undefined;
};

/**
 * "¿Te parece grave?" -> `criterio_preliminar`.
 *
 * YA NO TRADUCE. El formulario guardaba 'si' | 'no' y aquí se convertían a
 * 'grave' | 'no_grave'; desde que la escala es catálogo (`criterio_preliminar`,
 * 25-ago-2026) el formulario guarda directamente el código que sirvió el servidor.
 * Traducirlo obligaría a mantener aquí una tabla de equivalencias que habría que
 * ampliar a mano cada vez que el administrador agrega una respuesta — es decir, el
 * redespliegue que el catálogo existe para evitar.
 *
 * Lo que sí queda, y por eso la función no desaparece: **el campo SIN CONTESTAR viaja
 * como ausencia**, `undefined` y no `''`. Ojo con la distinción, que cambió el
 * 25-ago-2026: "Prefiero no opinar" YA NO es eso — es una respuesta del catálogo
 * (`prefiero_no_opinar`) y viaja como cualquier otra. La ausencia significa ahora que
 * nadie contestó la pregunta, que es lo que pasa con un borrador a medio llenar.
 */
export function criterioPreliminarDeFormulario(graveSugerido: unknown) {
  const codigo = String(graveSugerido ?? '').trim();
  return codigo || undefined;
}

/**
 * Fecha del `<input type="date">` (YYYY-MM-DD) -> el instante de su MEDIANOCHE EN LA ZONA DEL
 * PAÍS DEL CASO, en ISO (RN-82, Bug #1086).
 *
 * Antes era la medianoche del NAVEGADOR: un caso de México registrado desde Lima quedaba a las
 * 23:00 del día anterior en México, y el servidor, que lee el día en la zona del país
 * (`dia_en_zona`), contaba el plazo desde ese día anterior. La zona es obligatoria a propósito:
 * quien llama tiene que decir de qué país es la fecha (`useZonaDelPais`); `null` = Lima, el
 * mismo respaldo del servidor.
 */
export function fechaAIso(fecha: unknown, zonaHoraria: string | null | undefined) {
  if (!fecha) return undefined;
  // Se normaliza a texto antes de mirarla: del formulario llega una cadena, pero al
  // retomar un caso puede venir ya como ISO completo, y `includes` solo existe en
  // cadenas. Antes esto se apoyaba en que nadie pasara otra cosa.
  const texto = String(fecha);
  if (texto.includes('T')) return texto;
  return instanteDeMedianoche(texto, zonaHoraria);
}

export function aNotificante(n: Record<string, string>) {
  if (!n?.nombre?.trim()) return undefined;
  return {
    nombre: n.nombre.trim(),
    profesion_o_rol: texto(n.rol),
    direccion: texto(n.direccion),
    telefono: texto(n.telefono),
    correo: texto(n.email),
    institucion: texto(n.institucion),
  };
}

export function aDuenio(c: Record<string, string | boolean>) {
  if (!c) return undefined;

  // ⚠ Si el dueno ES el notificante, sus campos van VACIOS. El dominio lo rechaza con
  // 422 si llegan llenos: "el dato no se duplica; en el modelo vive una sola vez y
  // esta bandera dice donde leerlo".
  //
  // La pantalla si los copia y los deja visibles y bloqueados —lo pide HU-02—, pero
  // eso es comportamiento de PANTALLA. Mandarlos tambien al servidor hacia que el
  // caso mas frecuente —marcar "son los mismos datos"— fallara SIEMPRE al guardar.
  if (c.esElNotificante) {
    // La razon social si viaja: es del dueno, no del notificante. Una empresa
    // ganadera puede ser la dueña aunque quien reporte sea su veterinario.
    return { es_el_notificante: true, razon_social: texto(c.razonSocial) };
  }

  // EL BLOQUE VIAJA SIEMPRE, aunque este vacio (reportado 2-sep-2026: "a veces se
  // guarda y a veces no; a veces se retira de `duenio` y a veces no").
  //
  // Antes se omitia si no habia nombre ni razon social, y omitir es "no lo toques"
  // para el servidor. Dos consecuencias, las dos intermitentes a ojos del registrador:
  //   · llenar solo la profesion, la direccion o el telefono NO se guardaba —el bloque
  //     no salia—, y con un nombre al lado si;
  //   · borrar el nombre y la razon social NO retiraba nada de la base —el bloque
  //     tampoco salia—, y borrar cualquier otro campo con el nombre puesto si.
  // Es la misma excepcion que `categorias` (T-48): aqui "vacio" y "sin tocar" NO se
  // pueden confundir sin dano, porque no hay otra forma de decirle al servidor
  // "quita el dueno". Un caso sin dueno queda con la fila en nulos —"no informado"—,
  // que es lo que el formulario en blanco significa.
  //
  // Y el nombre ya no se rellena con la razon social: el registrador la veia copiada
  // en "Nombre y apellido" al retomar, sin haberla escrito ahi. El servidor no exige
  // nombre; cada campo lleva lo suyo o nada.
  return {
    es_el_notificante: false,
    nombre: texto(c.nombre),
    razon_social: texto(c.razonSocial),
    profesion_o_rol: texto(c.rol),
    direccion: texto(c.direccion),
    telefono: texto(c.telefono),
    correo: texto(c.email),
  };
}

/**
 * Los animales del caso. **LISTA desde el 26-ago-2026**, antes era uno solo.
 *
 * Se descartan las filas SIN ESPECIE, que es el único obligatorio del animal: una fila
 * recién agregada y todavía vacía no es un animal, y mandarla crearía en la base un
 * `animal_afectado` sin nada dentro que después nadie sabría interpretar.
 *
 * SIN `orden`, y no es un olvido: a diferencia de los medicamentos, el backend numera
 * los animales POR POSICIÓN en el servidor (`a_animal(a, orden=i)` sobre la lista tal
 * como llega), así que mandarlo no aporta nada — y `AnimalEntrada` es estricto
 * (`extra='forbid'`): incluirlo hacía 422 CUALQUIER guardado con animales llenos, borrador
 * incluido (27-ago-2026). El orden de captura se preserva igual: por la posición.
 */
export function aAnimales(animales: AnimalDelFormulario[]) {
  if (!Array.isArray(animales)) return undefined;
  const utiles = animales.filter((a) => a?.especie);
  if (!utiles.length) return undefined;
  return utiles.map((a) => ({
    especie: texto(a.especie),
    raza: texto(a.raza),
    sexo: texto(a.sexo),
    estado_fisiologico: texto(a.estadoFisiologico),
    edad: texto(a.edad),
    peso: texto(a.peso),
    motivo_tratamiento: texto(a.motivo),
    estado_salud: texto(a.estadoSalud),
    // Conteos de ESTE grupo (HU-03, 2-sep-2026), enteros como los del evento.
    cantidad_tratados: entero(a.tratados),
    cantidad_con_signos: entero(a.conSignos),
    cantidad_muertos: entero(a.muertos),
  }));
}

/**
 * Los medicamentos del caso. LA LISTA VACIA VIAJA (reportado 3-sep-2026: "elimino un
 * medicamento del borrador y sigue en `medicamento_implicado`").
 *
 * Se omitia cuando no quedaba ninguno, y omitir es "no lo toques" para el servidor:
 * quitar el ultimo medicamento —o el unico— no borraba nada en la base, mientras que
 * quitar uno de varios si (la lista con los demas viaja y el servidor reemplaza). El
 * servidor ya sabe que `[]` significa "sin medicamentos": borra las filas y no inserta
 * nada. Misma excepcion que `categorias` (T-48) y `duenio` (2-sep-2026).
 */
export function aMedicamentos(medicamentos: Record<string, string>[]) {
  if (!Array.isArray(medicamentos)) return undefined;
  return medicamentos
    .filter((m) => m?.nombre?.trim())
    .map((m, i) => ({
      // `orden` importa: sin el, releer el caso reordena los medicamentos y la
      // clave de duplicado de RN-11 puede cambiar entre lecturas.
      orden: i + 1,
      nombre_producto: m.nombre.trim(),
      producto_sku: texto(m.sku),
      forma_farmaceutica: texto(m.forma),
      concentracion: texto(m.concentracion),
      registro_sanitario: texto(m.registro),
      lote: texto(m.lote),
      fecha_vencimiento: texto(m.vencimiento),
      dosis: texto(m.dosis),
      via_administracion: texto(m.via),
      frecuencia: texto(m.frecuencia),
      duracion_tratamiento: texto(m.duracion),
      fecha_inicio: texto(m.fechaInicio),
      fecha_fin: texto(m.fechaFin),
      almacenamiento: texto(m.almacenamiento),
      administrado_por: texto(m.administro),
      es_sospechoso: siNo(m.esCausa),
      otros_medicamentos_simultaneos: siNo(m.masMedicamentos),
    }));
}

/**
 * Seccion 6 -> bloque `evento_adverso` del `PUT`.
 *
 * VIAJA EN CUANTO TIENE ALGO DENTRO (3-sep-2026, CP-05-06). Exigia descripcion Y fecha
 * de inicio, y sin las dos omitia el bloque entero: quien escribia primero los conteos
 * —o la severidad, o el desenlace— veia "Guardado" y los perdia al reabrir. HU-05 pide
 * esos dos campos "al enviar", no para guardar, y el servidor ya no los exige aqui.
 *
 * Es la vuelta que faltaba de T-48, que saco las categorias de este bloque por la misma
 * razon: la seccion 3 no podia guardarse hasta que la 6 estuviera completa.
 *
 * Sigue devolviendo `undefined` con la seccion ENTERAMENTE vacia, que es "no lo toques".
 */
export function aEventoAdverso(evento: EventoDelFormulario, zonaHoraria: string | null | undefined) {
  if (!evento) return undefined;
  const bloque = {
    descripcion: texto(evento.detalle),
    // RN-82: el día que se eligió, a medianoche del país del caso (como la recepción).
    fecha_inicio: fechaAIso(evento.inicio, zonaHoraria),
    tiempo_hasta_aparicion: numero(evento.tiempoAparicion),
    unidad_aparicion: texto(evento.unidadAparicion),
    duracion: numero(evento.duracion),
    unidad_duracion: texto(evento.unidadDuracion),
    severidad: texto(evento.severidad),
    desenlace: texto(evento.desenlace),
    cantidad_tratados: entero(evento.tratados),
    cantidad_con_signos: entero(evento.conSignos),
    cantidad_muertos: entero(evento.muertos),
  };
  // LAS UNIDADES NO CUENTAN para decidir si hay algo que guardar: nacen con un valor
  // por omision ('horas' y 'dias') que el registrador no eligio. Contandolas, un
  // formulario recien abierto mandaba un bloque de evento con solo dos unidades dentro
  // —una fila que finge existir— y la seccion 6 dejaba de significar "vacia".
  const { unidad_aparicion: _ua, unidad_duracion: _ud, ...loQueElUsuarioEscribio } = bloque;
  const hayAlgo = Object.values(loQueElUsuarioEscribio).some((v) => v !== undefined);
  return hayAlgo ? bloque : undefined;
}

/**
 * Seccion 3 del formulario -> bloque propio del `PUT` (T-48).
 *
 * `undefined` significa "no lo toques" en este cuerpo, igual que el resto de bloques.
 * La lista VACIA no es lo mismo: es "desmarque todas" y SI tiene que viajar, o la
 * ultima casilla que el registrador quita se quedaria marcada en la base.
 */
export function aCategorias(categorias: string[]) {
  return Array.isArray(categorias) ? categorias : undefined;
}

/**
 * Dato L2 (Ley 29733). Solo viaja si el caso declara la categoria de personas:
 * el backend lo exige con `tg_afectacion_exige_categoria` y responder 500 por eso
 * seria un error nuestro, no del usuario.
 */
export function aAfectacionPersona(personas: PersonaDelFormulario | null, categorias: string[]) {
  const declaraPersonas = (categorias ?? []).includes('seguridad_en_personas');
  if (!declaraPersonas || !personas) return undefined;
  // Minimizacion (RNF-02): lo que identifica a la persona solo viaja si ella lo
  // autorizo. Sin consentimiento se conserva el hecho clinico —que es lo que la
  // farmacovigilancia necesita— y se descarta lo que permite reconocerla.
  const autorizo = Boolean(personas.consentimiento);
  return {
    consentimiento_informado: autorizo,
    iniciales: autorizo ? texto(personas.iniciales) : undefined,
    ocupacion: autorizo ? texto(personas.ocupacion) : undefined,
    edad: entero(personas.edad),
    sexo: texto(personas.sexo),
    descripcion_afectacion: texto(personas.descripcion),
    dosis_recibida: texto(personas.dosisRecibida),
    vias_exposicion: Array.isArray(personas.vias) ? personas.vias : [],
    sitio_inyeccion: texto(personas.sitioInyeccion),
    // Que fue ese "Otro" (CP-06-13). Solo viajan si su casilla esta marcada: si el
    // registrador se arrepiente, el texto no debe sobrevivir a la respuesta que lo pedia.
    via_otros_detalle: (personas.vias || []).includes('otros') ? texto(personas.viaOtrosDetalle) : undefined,
    sitio_otro_detalle: personas.sitioInyeccion === 'otro' ? texto(personas.sitioOtroDetalle) : undefined,
    desenlace: texto(personas.desenlace),
    atencion_medica: siNo(personas.atencionMedica),
  };
}

/**
 * El caso entero -> cuerpo del `PUT`.
 *
 * Devuelve solo lo que tiene contenido: las claves ausentes NO se tocan en el
 * servidor, que es lo que permite que el autoguardado mande una seccion suelta.
 *
 * `zonaHoraria` es la del país del caso (`useZonaDelPais()(caso.pais)`): las fechas sin hora
 * del formulario viajan como su medianoche en esa zona (RN-82). `null` = Lima.
 */
export function aCuerpoDeActualizacion(caso: CasoDelFormulario, zonaHoraria: string | null | undefined) {
  // LAS CATEGORIAS SON UN BLOQUE PROPIO desde T-48, y con eso desaparecio el enredo que
  // ocupaba este comentario: viajaban dentro de `evento_adverso`, que solo se manda con
  // descripcion y fecha de inicio puestas, asi que la seccion 3 quedaba rehen de la 6.
  //
  // Producia dos fallos a la vez. El reportado: marcar una categoria disparaba el `PUT`,
  // el servidor respondia 200 y `evento_categoria` seguia vacia — guardado silencioso
  // que HU-36 prohibe por escrito. Y uno anterior (27-ago-2026): con "seguridad en
  // personas" marcada y la seccion 6 vacia, el dato L2 viajaba SIN la categoria que lo
  // habilita, el servidor devolvia 422 por RN-09 y se caia el cuerpo ENTERO —
  // notificante, animales, medicamentos—, asi que el borrador dejaba de guardarse sin
  // que el registrador hubiera pulsado nada.
  //
  // Ahora cada bloque viaja cuando su seccion tiene contenido, y la categoria acompana
  // al dato L2 en el mismo `PUT` porque ya no depende de nada mas. El backend aplica las
  // categorias ANTES que la afectacion (ver `actualizar_borrador`), que es lo que
  // permite mandarlos juntos.
  const cuerpo = {
    // LOS DOS DE LA SECCION 1, y hasta el 28-ago-2026 NO VIAJABAN (T-44 §2).
    //
    // `POST /casos` los pide para abrir el borrador y ahi se quedaban: cambiarlos
    // despues —el registrador elige Peru por defecto y luego cae en que el evento fue
    // en Colombia, o corrige la fecha en que le llego el aviso— movia el formulario y
    // no la base. El caso seguia con el pais y la fecha del primer segundo, que son los
    // que deciden autoridad, plazo regulatorio y correlativo (RN-06, RN-07). Se
    // descubria al retomar el borrador: la pantalla volvia a mostrar lo de antes.
    //
    // El servidor solo los acepta MIENTRAS el caso es borrador: despues del envio el
    // numero ya esta emitido contra la serie de un pais y el reloj del plazo ya corre.
    pais_evento: texto(caso.pais),
    fecha_recepcion: fechaAIso(caso.fechaRecepcion, zonaHoraria),
    criterio_preliminar: criterioPreliminarDeFormulario(caso.graveSugerido),
    declaracion_responsable: texto(caso.declaracion),
    notificante: aNotificante(caso.notificante),
    duenio: aDuenio(caso.cliente),
    animales: aAnimales(caso.animales),
    medicamentos: aMedicamentos(caso.medicamentos),
    evento_adverso: aEventoAdverso(caso.evento, zonaHoraria),
    categorias: aCategorias(caso.categorias),
    afectacion_persona: aAfectacionPersona(caso.personas, caso.categorias),
  };
  return Object.fromEntries(Object.entries(cuerpo).filter(([, v]) => v !== undefined));
}

// ---------------------------------------------------------------------------
// El camino INVERSO: del `GET /casos/{id}` a la forma del formulario.
// ---------------------------------------------------------------------------

/**
 * ISO con zona -> fecha del `<input type="date">`. Inverso de `fechaAIso`.
 *
 * El día se lee en la ZONA DEL PAÍS DEL CASO (RN-82), la misma con la que `fechaAIso` construyó
 * la medianoche: solo así vuelve el mismo día. Leerlo en la zona del navegador devolvía el día
 * anterior a un caso de Chile visto desde Lima (su medianoche son las 22:00 o 23:00 de Lima), y
 * recortar el texto UTC daría el día de Greenwich.
 */
export function isoAFecha(iso: unknown, zonaHoraria: string | null | undefined): string {
  if (!iso) return '';
  const texto = String(iso);
  // Ya es date-only (los medicamentos viajan asi): se devuelve tal cual.
  if (!texto.includes('T')) return texto;
  const fecha = new Date(texto);
  if (Number.isNaN(fecha.getTime())) return '';
  return fechaIsoDeInstante(fecha.toISOString(), zonaHoraria);
}

/** `null`/ausencia del API -> cadena vacia, que es como el formulario dice "sin llenar". */
const deTexto = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/** Numero del API -> texto del `<input>`. Los campos numericos del formulario son texto. */
const deNumero = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

// Booleano del API -> el valor del radio. Como TABLA y no como ternarios encadenados:
// lo que importa es que son tres casos —sí, no y "nadie contestó"— y en la cadena el
// tercero quedaba escondido detrás de dos condiciones.
//
// `Map` y no un objeto literal: las claves son los booleanos `true`/`false`, y un
// objeto los convertiria a las cadenas 'true'/'false'. Cualquier otro valor —`null`,
// `undefined`, lo que sea— no esta en la tabla y devuelve la cadena vacia, que es como
// el formulario dice "sin responder".
const SI_NO_MAYUSCULA = new Map([[true, 'Sí'], [false, 'No']]);
const SI_NO_MINUSCULA = new Map([[true, 'si'], [false, 'no']]);

/** Booleano del API -> radios 'Sí'/'No' de la seccion 5. `null` = nadie contesto. */
const deSiNoMayuscula = (v: boolean | null | undefined): string => (typeof v === 'boolean' ? SI_NO_MAYUSCULA.get(v) : undefined) ?? '';

/** Booleano del API -> radios 'si'/'no' de la seccion 7 (`OPCIONES_SI_NO`). */
const deSiNoMinuscula = (v: boolean | null | undefined): string => (typeof v === 'boolean' ? SI_NO_MINUSCULA.get(v) : undefined) ?? '';

// Identificador local de las filas repetibles que llegan del servidor. El espejo
// local (formato 2) y las `key` de React lo exigen; el que genera `RegistroCaso`
// usa el prefijo `f`, este usa `srv` para que nunca choquen al agregar filas
// nuevas sobre un caso retomado. NO viaja al API: el mapeo de ida construye el
// cuerpo campo a campo y nunca copia el objeto.
let _filaDelServidor = 0;
const idDeFilaDelServidor = () => `srv${++_filaDelServidor}`;

/**
 * El caso tal como lo devuelve `GET /casos/{id}` -> la forma del formulario.
 *
 * Es el inverso de `aCuerpoDeActualizacion`, y vive AQUI —junto a el— por la misma
 * razon que aquel: repartir la traduccion por las pantallas es como se pierden
 * campos en silencio. Lo usa "Retomar" desde la bandeja (T-37, MG-ILE-471): siembra
 * el espejo local con esto y el registro se rehidrata por el camino que ya existia.
 *
 * Decisiones que no se ven a simple vista:
 *   · Los codigos de catalogo viajan TAL CUAL ('bovinos', 'grave', 'muerte'…): los
 *     Selects del formulario trabajan con codigos {id,label}, no con etiquetas.
 *   · La recepcion y el inicio del evento llegan ISO con zona y el input es date-only ->
 *     `isoAFecha`, leidas en la zona del pais del caso (`zonaHoraria`, RN-82).
 *     Las de medicamentos ya son date-only y pasan sin tocar.
 *   · La seccion 7 (personas, dato L2) SOLO se mapea si el API la sirve; esa ruta
 *     hoy no la devuelve (RNF-03) y `tiene_dato_de_personas` lo avisa. Dejarla en
 *     `null` no la borra en el servidor: el cuerpo de ida la omite, y omitir es
 *     "no lo toques".
 *   · Los medicamentos se ordenan por su `orden` antes de mapear: es la clave de
 *     duplicado de RN-11 y el orden de captura que el registrador reconoce.
 */
/**
 * La respuesta cruda de `GET /casos/{id}`.
 *
 * Es el UNICO `any` que queda en el modulo, y esta aqui a proposito: esta funcion lee
 * decenas de campos anidados de una respuesta que todavia no esta tipada entera, y con
 * `unknown` habria que afirmar el tipo en cada uno de esos accesos, una linea de ruido
 * por campo leido. El dia que exista el tipo completo del caso del servidor, se cambia
 * aqui y en ningun otro sitio.
 */
type CasoDelApi = RespuestaDelApi;

export function desdeCaso(casoDelApi: CasoDelApi, zonaHoraria: string | null | undefined) {
  const n = casoDelApi.notificante;
  const d = casoDelApi.duenio;
  const e = casoDelApi.evento_adverso;
  const p = casoDelApi.afectacion_persona;

  return {
    // `id` del formulario era el numero calculado por el prototipo; hoy la identidad
    // es el uuid del servidor y lo lleva quien habla con la API, no el formulario.
    id: null,
    // Los dos de la seccion 1 (T-44 §2): se rehidratan para que retomar un borrador
    // muestre el pais y la fecha CON LOS QUE SE GUARDO, y el `PUT` siguiente vuelva a
    // mandar esos mismos. `deTexto` y no el valor crudo: sin pais, el `Select` pasaria
    // de controlado a no controlado y React avisa por consola en vez de mostrar el
    // marcador de posicion.
    pais: deTexto(casoDelApi.pais_evento),
    estado: casoDelApi.estado ?? 'borrador',
    grave: null,
    fechaRecepcion: isoAFecha(casoDelApi.fecha_recepcion, zonaHoraria),
    fechaRegistro: casoDelApi.fecha_registro ?? null,
    // Del CASO y ya no del evento adverso (T-48): un borrador con la seccion 3 marcada
    // y la 6 vacia tiene categorias guardadas y ningun `evento_adverso` que las lleve.
    categorias: Array.isArray(casoDelApi.categorias) ? [...casoDelApi.categorias] : [],
    // EL SERVIDOR TIENE DATO L2 QUE ESTA PANTALLA NO PUEDE VER, y hay que saberlo.
    //
    // La seccion 7 no viaja en el GET a proposito (RNF-03), asi que `personas` queda en
    // `null` al retomar. Hasta T-48b eso era inofensivo. Ya no: desmarcar la categoria de
    // personas ORDENA la purga del bloque L2, y la advertencia previa que HU-06 exige
    // ("el sistema advierte y, CONFIRMADO, deja de exigir esos datos") se decidia mirando
    // `personas` — que aqui esta vacio. Resultado: al retomar un borrador se borraba dato
    // de salud sin preguntar nada, que es justo lo que HU-06 prohibe.
    //
    // `tiene_dato_de_personas` es el aviso que el API ya servia para esto. Se mapea aqui y
    // lo consume `datosEnRiesgoAlConmutar`.
    tieneDatoDePersonasEnServidor: Boolean(casoDelApi.tiene_dato_de_personas),
    graveSugerido: deTexto(casoDelApi.criterio_preliminar),
    notificante: {
      nombre: deTexto(n?.nombre),
      direccion: deTexto(n?.direccion),
      telefono: deTexto(n?.telefono),
      email: deTexto(n?.correo),
      rol: deTexto(n?.profesion_o_rol),
      institucion: deTexto(n?.institucion),
    },
    cliente: {
      esElNotificante: Boolean(d?.es_el_notificante),
      razonSocial: deTexto(d?.razon_social),
      nombre: deTexto(d?.nombre),
      direccion: deTexto(d?.direccion),
      telefono: deTexto(d?.telefono),
      email: deTexto(d?.correo),
      rol: deTexto(d?.profesion_o_rol),
    },
    animales: (casoDelApi.animales ?? []).map((a: CasoDelApi) => ({
      id: idDeFilaDelServidor(),
      especie: deTexto(a.especie),
      raza: deTexto(a.raza),
      sexo: deTexto(a.sexo),
      estadoFisiologico: deTexto(a.estado_fisiologico),
      edad: deTexto(a.edad),
      peso: deTexto(a.peso),
      motivo: deTexto(a.motivo_tratamiento),
      estadoSalud: deTexto(a.estado_salud),
      tratados: deNumero(a.cantidad_tratados),
      conSignos: deNumero(a.cantidad_con_signos),
      muertos: deNumero(a.cantidad_muertos),
    })),
    medicamentos: [...(casoDelApi.medicamentos ?? [])]
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
      .map((m) => ({
        id: idDeFilaDelServidor(),
        sku: deTexto(m.producto_sku),
        nombre: deTexto(m.nombre_producto),
        forma: deTexto(m.forma_farmaceutica),
        concentracion: deTexto(m.concentracion),
        registro: deTexto(m.registro_sanitario),
        lote: deTexto(m.lote),
        vencimiento: deTexto(m.fecha_vencimiento),
        dosis: deTexto(m.dosis),
        via: deTexto(m.via_administracion),
        frecuencia: deTexto(m.frecuencia),
        duracion: deTexto(m.duracion_tratamiento),
        fechaInicio: deTexto(m.fecha_inicio),
        fechaFin: deTexto(m.fecha_fin),
        almacenamiento: deTexto(m.almacenamiento),
        administro: deTexto(m.administrado_por),
        esCausa: deSiNoMayuscula(m.es_sospechoso),
        masMedicamentos: deSiNoMayuscula(m.otros_medicamentos_simultaneos),
      })),
    evento: {
      inicio: isoAFecha(e?.fecha_inicio, zonaHoraria),
      tiempoAparicion: deNumero(e?.tiempo_hasta_aparicion),
      // Las unidades por defecto son las mismas con las que nace el formulario
      // (`vacio()`): un caso guardado sin unidad se ve igual que uno recien abierto.
      unidadAparicion: deTexto(e?.unidad_aparicion) || 'horas',
      duracion: deNumero(e?.duracion),
      unidadDuracion: deTexto(e?.unidad_duracion) || 'dias',
      severidad: deTexto(e?.severidad),
      desenlace: deTexto(e?.desenlace),
      tratados: deNumero(e?.cantidad_tratados),
      conSignos: deNumero(e?.cantidad_con_signos),
      muertos: deNumero(e?.cantidad_muertos),
      detalle: deTexto(e?.descripcion),
    },
    personas: p
      ? {
          consentimiento: Boolean(p.consentimiento_informado),
          iniciales: deTexto(p.iniciales),
          vias: Array.isArray(p.vias_exposicion) ? [...p.vias_exposicion] : [],
          sitioInyeccion: deTexto(p.sitio_inyeccion),
          viaOtrosDetalle: deTexto(p.via_otros_detalle),
          sitioOtroDetalle: deTexto(p.sitio_otro_detalle),
          dosisRecibida: deTexto(p.dosis_recibida),
          sexo: deTexto(p.sexo),
          edad: deNumero(p.edad),
          ocupacion: deTexto(p.ocupacion),
          descripcion: deTexto(p.descripcion_afectacion),
          atencionMedica: deSiNoMinuscula(p.atencion_medica),
          desenlace: deTexto(p.desenlace),
        }
      : null,
    // Los adjuntos NO se reconstruyen aqui: los administra `useAdjuntos` contra el
    // servidor, y `evidencias` es la lista local del modo prototipo.
    evidencias: [],
    declaracion: deTexto(casoDelApi.declaracion_responsable),
    evaluacion: null,
    informe: null,
    notificacion: null,
    seguimiento: null,
    historial: [],
  };
}
